import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import LeadCard, { Lead } from './LeadCard';
import type { LeadStatus } from './stageValidation';

interface PipelineColumnProps {
  status: LeadStatus;
  leads: Lead[];
  onEditLead: (lead: Lead) => void;
  onViewLead: (lead: Lead) => void;
  onViewActivity: (lead: Lead) => void;
  getAssigneeName?: (userId: string | null) => string | null;
}

const columnConfig: Record<LeadStatus, { label: string; headerBg: string; headerText: string; borderColor: string }> = {
  new: {
    label: 'New',
    headerBg: 'bg-purple-100 dark:bg-purple-900/30',
    headerText: 'text-purple-700 dark:text-purple-300',
    borderColor: 'border-purple-200 dark:border-purple-800',
  },
  contacted: {
    label: 'Contacted',
    headerBg: 'bg-blue-100 dark:bg-blue-900/30',
    headerText: 'text-blue-700 dark:text-blue-300',
    borderColor: 'border-blue-200 dark:border-blue-800',
  },
  demo_scheduled: {
    label: 'Demo Scheduled',
    headerBg: 'bg-amber-100 dark:bg-amber-900/30',
    headerText: 'text-amber-700 dark:text-amber-300',
    borderColor: 'border-amber-200 dark:border-amber-800',
  },
  demo_done: {
    label: 'Demo Done',
    headerBg: 'bg-sage/20',
    headerText: 'text-sage',
    borderColor: 'border-sage/30',
  },
  converted: {
    label: 'Converted',
    headerBg: 'bg-green-100 dark:bg-green-900/30',
    headerText: 'text-green-700 dark:text-green-300',
    borderColor: 'border-green-200 dark:border-green-800',
  },
  lost: {
    label: 'Lost',
    headerBg: 'bg-stone-200 dark:bg-stone-700',
    headerText: 'text-stone-600 dark:text-stone-400',
    borderColor: 'border-stone-300 dark:border-stone-600',
  },
};

export default function PipelineColumn({ status, leads, onEditLead, onViewLead, onViewActivity, getAssigneeName }: PipelineColumnProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: status,
  });

  const config = columnConfig[status];
  const leadIds = leads.map(lead => lead.id);

  return (
    <div
      className={`flex flex-col flex-1 min-w-[200px] bg-stone-50 dark:bg-stone-900/50 rounded-xl border ${config.borderColor} ${
        isOver ? 'ring-2 ring-sage ring-opacity-50 bg-sage/5' : ''
      } transition-all`}
    >
      {/* Column Header */}
      <div className={`flex items-center justify-between px-3 py-2.5 rounded-t-xl ${config.headerBg}`}>
        <h3 className={`font-semibold text-sm ${config.headerText}`}>
          {config.label}
        </h3>
        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${config.headerBg} ${config.headerText}`}>
          {leads.length}
        </span>
      </div>

      {/* Cards Container */}
      <div
        ref={setNodeRef}
        className="flex-1 p-2 space-y-2 overflow-y-auto max-h-[calc(100vh-350px)] lg:max-h-[calc(100vh-380px)] min-h-[150px]"
      >
        <SortableContext items={leadIds} strategy={verticalListSortingStrategy}>
          {leads.length > 0 ? (
            leads.map((lead) => (
              <LeadCard
                key={lead.id}
                lead={lead}
                onEdit={onEditLead}
                onView={onViewLead}
                onViewActivity={onViewActivity}
                getAssigneeName={getAssigneeName}
              />
            ))
          ) : (
            <div className="flex items-center justify-center h-24 text-stone-400 dark:text-stone-500 text-sm">
              No leads
            </div>
          )}
        </SortableContext>
      </div>
    </div>
  );
}
